import { useEffect } from "react";

import type { LegacyMatch } from "../api/legacyTypes";
import { EntityLink } from "./EntityLink";

function duration(seconds?: number) {
  if (!seconds) return "Not finished";
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

export function DotaMatchPreview({ match, tournamentId, onClose }: { match: LegacyMatch; tournamentId: string; onClose: () => void }) {
  useEffect(() => {
    const close = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onClose]);
  const slug = tournamentId.split("/").at(-1) ?? "";
  const href = `/tournaments/dota2/${slug}/matches/${encodeURIComponent(match.sourceKey)}`;
  const games = match.dota?.games ?? [];
  return (
    <div className="match-preview-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <aside className="match-preview" role="dialog" aria-modal="true" aria-label={`${match.participant1} versus ${match.participant2} quick stats`}>
        <header>
          <div><small>{match.round || match.bracket}</small><strong>Best of {match.dota?.bestOf || 3}</strong></div>
          <button type="button" aria-label="Close match preview" onClick={onClose}>×</button>
        </header>
        <div className="match-preview-score">
          <EntityLink gameId="dota2" kind="team" name={match.participant1 || "TBD"} entityId={match.teamId1} logoUrl={match.logo1} />
          <span><b>{match.score1 || "–"}</b><em>:</em><b>{match.score2 || "–"}</b></span>
          <EntityLink gameId="dota2" kind="team" name={match.participant2 || "TBD"} entityId={match.teamId2} logoUrl={match.logo2} />
        </div>
        {games.length > 0 ? <div className="match-preview-games">
          {games.map((game) => <article key={game.number}>
            <span>Game {game.number}</span><strong>{duration(game.durationSeconds)}</strong>
            {game.matchId ? <small>#{game.matchId}</small> : <small>Game ID pending</small>}
            <div className="match-preview-draft">{game.picks.slice(0, 10).map((pick) => pick.imageUrl ? <img key={`${pick.teamSlot}:${pick.order}`} src={pick.imageUrl} alt={pick.hero} title={pick.hero} /> : <span key={`${pick.teamSlot}:${pick.order}`} title={pick.hero}>{pick.hero.slice(0, 3).toUpperCase()}</span>)}</div>
          </article>)}
        </div> : <p className="match-preview-empty">Game IDs and draft will appear when Liquipedia publishes them.</p>}
        {match.dota?.casters?.length ? <p className="match-preview-casters">Cast by {match.dota.casters.join(" · ")}</p> : null}
        <a className="button-primary match-preview-open" href={href}>Open detailed match</a>
      </aside>
    </div>
  );
}
